import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, qs } from './api.js'

/**
 * One data-fetching hook, so every page renders the same five states (rule 19).
 *
 * The old admin pages each hand-rolled their loading and error handling, which is how "no data yet" and "the
 * request failed" came to look identical. Here a caller gets `{ data, loading, error, reload }` and the shared
 * components turn each into a distinct screen — including permission-denied, which the M9 audit found being
 * reported as a connection problem.
 *
 * Aborts in flight on unmount or when the query changes, so switching the date range twice quickly cannot let
 * the first response overwrite the second.
 */
export function useApi(path, { skip = false } = {}) {
  const [state, setState] = useState({ data: null, loading: !skip, error: null })
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (skip) {
      setState({ data: null, loading: false, error: null })
      return undefined
    }
    const ctrl = new AbortController()
    let live = true
    setState((s) => ({ ...s, loading: true, error: null }))
    api(path, { signal: ctrl.signal })
      .then((data) => { if (live) setState({ data, loading: false, error: null }) })
      .catch((error) => {
        // An aborted request is not a failure — it means the user moved on.
        if (error.name === 'AbortError' || !live) return
        setState({ data: null, loading: false, error })
      })
    return () => { live = false; ctrl.abort() }
  }, [path, skip, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { ...state, reload }
}

/**
 * The dashboard's period selector, shared so every chart on a page covers the same window.
 *
 * Ranges are named in the vocabulary a maintenance manager uses — a shift, a week, a month, a quarter — rather
 * than in raw day counts. 90 days is the default because it matches the backend's own default window, so a
 * client that omits dates and one that sends them agree.
 */
export const RANGES = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: '365', label: 'Last 12 months', days: 365 },
]

export function useRange(initial = '90') {
  const [key, setKey] = useState(initial)
  const [department, setDepartment] = useState('')

  const params = useMemo(() => {
    const days = RANGES.find((r) => r.key === key)?.days ?? 90
    const to = new Date()
    const from = new Date(to.getTime() - days * 86400000)
    // ISO with an offset, which is what @DateTimeFormat(ISO.DATE_TIME) parses into OffsetDateTime. A bare
    // local string is rejected, and a Z-suffixed one silently shifts the window by the plant's UTC offset.
    return { from: iso(from), to: iso(to), department: department || undefined }
  }, [key, department])

  return { key, setKey, department, setDepartment, params, query: qs(params) }
}

function iso(d) {
  const pad = (n) => String(n).padStart(2, '0')
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? '+' : '-'
  const a = Math.abs(off)
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` +
    `${sign}${pad(Math.floor(a / 60))}:${pad(a % 60)}`
  )
}
